import { c as _c } from "react/compiler-runtime";
import { postEntitiesByFpIdActionsMutation, postEntitiesByFpIdTokenMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoInfo16 } from '@onefootprint/icons';
import type { Annotation, WorkflowTriggered } from '@onefootprint/request-types/dashboard';
import { Dialog, LinkButton } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionGate from '@/components/permission-gate';
import useDisplayLinkDialog from '../../../../../vault-actions/components/person-actions/components/request-more-info-dialog/hooks/use-display-link-dialog';
import AnnotationNote from '../annotation-note';
import EventBodyEntry from '../event-body-entry';
type WorkflowTriggeredEventBodyProps = {
  data: WorkflowTriggered;
  entityId: string;
};
const WorkflowTriggeredEventBody = t0 => {
  const $ = _c(64);
  const {
    data,
    entityId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "auditTrail.timeline.workflowTriggeredEvent"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const queryClient = useQueryClient();
  const userEntityId = data.fpId || entityId;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = postEntitiesByFpIdTokenMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const generateTokenMutation = useMutation(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = postEntitiesByFpIdActionsMutation();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const actionsMutation = useMutation(t3);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsDialogOpen(false);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleClose = t4;
  const showErrorToast = useRequestErrorToast();
  let t5;
  if ($[4] !== generateTokenMutation.data || $[5] !== userEntityId) {
    t5 = {
      linkData: generateTokenMutation.data,
      onClose: handleClose,
      entityId: userEntityId
    };
    $[4] = generateTokenMutation.data;
    $[5] = userEntityId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    title,
    primaryButton,
    secondaryButton,
    component
  } = useDisplayLinkDialog(t5);
  if (!data.requestIsActive) {
    return null;
  }
  let t6;
  if ($[7] !== generateTokenMutation || $[8] !== showErrorToast || $[9] !== userEntityId) {
    t6 = async () => {
      setIsDialogOpen(true);
      ;
      try {
        await generateTokenMutation.mutateAsync({
          body: {
            kind: "inherit",
            sendLinkTo: []
          },
          path: {
            fpId: userEntityId
          }
        });
      } catch (t7) {
        const error = t7;
        showErrorToast(error);
        setIsDialogOpen(false);
      }
    };
    $[7] = generateTokenMutation;
    $[8] = showErrorToast;
    $[9] = userEntityId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const openLinkDialog = t6;
  let t7;
  if ($[11] !== actionsMutation || $[12] !== data.wfrId || $[13] !== queryClient || $[14] !== showErrorToast || $[15] !== userEntityId) {
    t7 = async () => {
      if (!data.wfrId) {
        return;
      }
      ;
      try {
        await actionsMutation.mutateAsync({
          body: {
            actions: [{
              kind: "cancel_request_for_info",
              wfrId: data.wfrId
            }]
          },
          path: {
            fpId: userEntityId
          }
        });
        queryClient.invalidateQueries();
      } catch (t8) {
        const error_0 = t8;
        showErrorToast(error_0);
      }
    };
    $[11] = actionsMutation;
    $[12] = data.wfrId;
    $[13] = queryClient;
    $[14] = showErrorToast;
    $[15] = userEntityId;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleCancelRequest = t7;
  let t8;
  if ($[17] !== data.actor || $[18] !== data.note) {
    t8 = data.note && <AnnotationNote annotation={{
      id: "",
      note: data.note,
      isPinned: false,
      source: data.actor,
      timestamp: ""
    } as Annotation} hidePinToggle={true} />;
    $[17] = data.actor;
    $[18] = data.note;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t(_temp);
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = <span className="text-primary text-body-2">{t9}</span>;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex items-center justify-center mx-0.5">·</div>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t(_temp2);
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = generateTokenMutation.isPending;
  let t14;
  if ($[27] !== t) {
    t14 = t(_temp3);
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== generateTokenMutation.isPending || $[30] !== openLinkDialog || $[31] !== t14) {
    t15 = <LinkButton onClick={openLinkDialog} disabled={t13} variant="label-2">{t14}</LinkButton>;
    $[29] = generateTokenMutation.isPending;
    $[30] = openLinkDialog;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t12 || $[34] !== t15) {
    t16 = <PermissionGate scopeKind="manual_review" fallbackText={t12} tooltipSide="left">{t15}</PermissionGate>;
    $[33] = t12;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex items-center justify-center mx-0.5">·</div>;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t) {
    t18 = t(_temp4);
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = actionsMutation.isPending;
  let t20;
  if ($[39] !== t) {
    t20 = t(_temp5);
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== actionsMutation.isPending || $[42] !== handleCancelRequest || $[43] !== t20) {
    t21 = <LinkButton onClick={handleCancelRequest} disabled={t19} variant="label-2">{t20}</LinkButton>;
    $[41] = actionsMutation.isPending;
    $[42] = handleCancelRequest;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t18 || $[46] !== t21) {
    t22 = <PermissionGate scopeKind="manual_review" fallbackText={t18} tooltipSide="left">{t21}</PermissionGate>;
    $[45] = t18;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t10 || $[49] !== t16 || $[50] !== t22) {
    t23 = <EventBodyEntry iconComponent={IcoInfo16} testID="workflow-triggered-event-body" content={<div className="flex gap-1">{t10}{t11}{t16}{t17}{t22}</div>} />;
    $[48] = t10;
    $[49] = t16;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t23 || $[53] !== t8) {
    t24 = <div className="flex flex-col">{t8}{t23}</div>;
    $[52] = t23;
    $[53] = t8;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== component || $[56] !== isDialogOpen || $[57] !== primaryButton || $[58] !== secondaryButton || $[59] !== title) {
    t25 = <Dialog size="compact" title={title} onClose={handleClose} open={isDialogOpen} primaryButton={primaryButton} secondaryButton={secondaryButton}>{component}</Dialog>;
    $[55] = component;
    $[56] = isDialogOpen;
    $[57] = primaryButton;
    $[58] = secondaryButton;
    $[59] = title;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t24 || $[62] !== t25) {
    t26 = <>{t24}{t25}</>;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  return t26;
};
export default WorkflowTriggeredEventBody;
function _temp($) {
  return $.linkGenerated;
}
function _temp2($_0) {
  return $_0.createLink.noPermission;
}
function _temp3($_1) {
  return $_1.createLink.label;
}
function _temp4($_2) {
  return $_2.cancelRequest.noPermission;
}
function _temp5($_3) {
  return $_3.cancelRequest.label;
}